import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { Send, MessageSquare, Copy, Check } from 'lucide-react';
import { generateWhatsAppUrl } from '@/utils/whatsappTemplates';
import { useAuth } from './AuthProvider';

interface DialogEnviarMensagemProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  pedidoId: string;
  clienteNome: string;
  clienteTelefone: string;
  pedidoCode: string;
  pedidoCompleto?: any;
}

type Step = 'selecionar-template' | 'editar-mensagem';

const TEMPLATES_DEFAULT = [
  {
    id: 'confirmacao-pedido',
    titulo: 'Confirmação de Pedido',
    descricao: 'Confirmação que o pedido foi recebido',
    mensagem: 'Olá {{nome}}! ✅ Seu pedido #{{codigo}} foi confirmado com sucesso! 🎉\n\nEstaremos trabalhando no seu projeto com muito carinho. Você receberá atualizações conforme o pedido avança.\n\nObrigado por confiar em nosso trabalho! 💪'
  },
  {
    id: 'pedido-pronto',
    titulo: 'Pedido Pronto',
    descricao: 'Notifica que o pedido está pronto para retirada',
    mensagem: 'Olá {{nome}}! 🎉 Seu pedido #{{codigo}} está pronto para retirada! Aguardamos você em nosso ateliê.'
  },
  {
    id: 'aguardando-retirada',
    titulo: 'Aguardando Retirada',
    descricao: 'Lembrete que o pedido está aguardando retirada',
    mensagem: 'Oi {{nome}}, tudo bem? 👋 Seu pedido #{{codigo}} está aqui, aguardando sua retirada. Passe quando puder!'
  },
  {
    id: 'informacoes-pedido',
    titulo: 'Informações do Pedido',
    descricao: 'Envia detalhes do pedido ao cliente',
    mensagem: 'Olá {{nome}}! 📋 Informações do seu pedido #{{codigo}}:\n\nAcompanhe o status e detalhes através do nosso sistema.\n\nQualquer dúvida, é só chamar! 😊'
  },
  {
    id: 'pagamento-pendente',
    titulo: 'Pagamento Pendente',
    descricao: 'Lembrete sobre saldo pendente do pedido',
    mensagem: 'Olá {{nome}}! 💰 Esse é um lembrete sobre o pagamento pendente do seu pedido #{{codigo}}.\n\nFico à disposição para facilitar essa transação com você. Qualquer dúvida, é só chamar!\n\nObrigado! 😊'
  }
];

export function DialogEnviarMensagem({
  open,
  onOpenChange,
  pedidoId,
  clienteNome,
  clienteTelefone,
  pedidoCode,
  pedidoCompleto
}: DialogEnviarMensagemProps) {
  const { empresa } = useAuth();
  const [step, setStep] = useState<Step>('selecionar-template');
  const [templateSelecionado, setTemplateSelecionado] = useState<typeof TEMPLATES_DEFAULT[0] | null>(null);
  const [mensagemPersonalizada, setMensagemPersonalizada] = useState('');
  const [copiado, setCopiado] = useState(false);

  const construirMensagemInformacoes = () => {
    let mensagem = `Olá ${clienteNome}! 📋\n\n`;
    mensagem += `*INFORMAÇÕES DO SEU PEDIDO #${pedidoCode}*\n\n`;

    // Tipo de pedido
    if (pedidoCompleto?.type) {
      mensagem += `📌 *Tipo:* ${pedidoCompleto.type}\n`;
    }

    // Descrição
    if (pedidoCompleto?.description) {
      mensagem += `📝 *Descrição:* ${pedidoCompleto.description}\n`;
    }

    // Valor
    if (pedidoCompleto?.value) {
      mensagem += `💰 *Valor:* R$ ${Number(pedidoCompleto.value).toFixed(2)}\n`;
    }

    // Data de entrega
    if (pedidoCompleto?.delivery) {
      mensagem += `📅 *Data de Entrega:* ${pedidoCompleto.delivery}\n`;
    }

    // Status
    if (pedidoCompleto?.status) {
      mensagem += `📌 *Status:* ${pedidoCompleto.status}\n`;
    }

    mensagem += `\nQualquer dúvida, é só chamar! 😊`;

    return mensagem;
  };

  const handleSelecionarTemplate = (template: typeof TEMPLATES_DEFAULT[0]) => {
    let mensagem = template.mensagem
      .replace('{{nome}}', clienteNome)
      .replace('{{codigo}}', pedidoCode);

    // Se for o template de informações do pedido, montar com dados reais
    if (template.id === 'informacoes-pedido') {
      mensagem = construirMensagemInformacoes();
    }

    // Adicionar endereço para templates que precisam (Pedido Pronto e Aguardando Retirada)
    if ((template.id === 'pedido-pronto' || template.id === 'aguardando-retirada') && empresa?.endereco) {
      mensagem += `\n\n📍 *Nos encontre aqui:*\n${empresa.endereco}`;
    }

    setTemplateSelecionado(template);
    setMensagemPersonalizada(mensagem);
    setStep('editar-mensagem');
  };

  const normalizeWhatsAppPhone = (phone?: string | null): string | undefined => {
    if (!phone) return undefined;
    const digits = String(phone).replace(/\D/g, "");
    if (!digits) return undefined;
    if (digits.startsWith("55")) return digits;
    if (digits.length === 10 || digits.length === 11) return `55${digits}`;
    return digits;
  };

  const handleCopiarEAbrir = async () => {
    try {
      // Copiar para clipboard
      await navigator.clipboard.writeText(mensagemPersonalizada);
      setCopiado(true);
      toast.success('Mensagem copiada! Abrindo WhatsApp...');

      // Normalizar telefone e abrir WhatsApp com a mensagem
      const normalizedPhone = normalizeWhatsAppPhone(clienteTelefone);
      const whatsappUrl = generateWhatsAppUrl(mensagemPersonalizada, normalizedPhone);
      window.open(whatsappUrl, '_blank');

      // Reset e fechar
      setTimeout(() => {
        setStep('selecionar-template');
        setTemplateSelecionado(null);
        setMensagemPersonalizada('');
        setCopiado(false);
        onOpenChange(false);
      }, 500);
    } catch (err) {
      toast.error('Erro ao copiar mensagem');
    }
  };

  if (step === 'selecionar-template') {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto p-3 sm:p-6">
          <DialogHeader className="space-y-2">
            <DialogTitle className="flex items-center gap-2 text-lg sm:text-xl">
              <MessageSquare className="w-4 h-4 sm:w-5 sm:h-5" />
              Enviar Mensagem
            </DialogTitle>
            <DialogDescription className="text-xs sm:text-sm">
              Selecione um template para {clienteNome}
            </DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 sm:gap-3">
            {TEMPLATES_DEFAULT.map((template) => (
              <Card
                key={template.id}
                className="cursor-pointer hover:border-blue-500 hover:shadow-md transition-all p-3"
                onClick={() => handleSelecionarTemplate(template)}
              >
                <CardHeader className="pb-2 p-0">
                  <CardTitle className="text-sm sm:text-base">{template.titulo}</CardTitle>
                  <CardDescription className="text-xs">{template.descricao}</CardDescription>
                </CardHeader>
                <CardContent className="p-0 mt-2">
                  <p className="text-xs text-gray-600 line-clamp-2">{template.mensagem}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          <DialogFooter className="gap-2 flex">
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="flex-1 sm:flex-none text-xs sm:text-sm"
            >
              Cancelar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  if (step === 'editar-mensagem') {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto p-3 sm:p-6">
          <DialogHeader className="space-y-2">
            <DialogTitle className="text-lg sm:text-xl">
              Editar - {templateSelecionado?.titulo}
            </DialogTitle>
            <DialogDescription className="text-xs sm:text-sm">
              Personalize antes de enviar para {clienteNome}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <div>
              <Label className="text-xs sm:text-sm">Telefone</Label>
              <Input
                value={clienteTelefone}
                disabled
                placeholder="(11) 99999-9999"
                className="bg-gray-100 h-8 text-xs sm:text-sm"
              />
            </div>

            <div>
              <Label className="text-xs sm:text-sm">Mensagem</Label>
              <Textarea
                value={mensagemPersonalizada}
                onChange={(e) => setMensagemPersonalizada(e.target.value)}
                placeholder="Mensagem..."
                rows={4}
                className="font-mono text-xs sm:text-sm resize-none"
              />
              <p className="text-xs text-gray-500 mt-1">
                {mensagemPersonalizada.length} caracteres
              </p>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded p-2 sm:p-3 text-xs sm:text-sm text-blue-800">
              <p className="font-semibold mb-1">Preview:</p>
              <p className="whitespace-pre-wrap max-h-32 overflow-y-auto text-xs">
                {mensagemPersonalizada}
              </p>
            </div>
          </div>

          <DialogFooter className="gap-2 flex flex-col-reverse sm:flex-row">
            <Button
              variant="outline"
              onClick={() => {
                setStep('selecionar-template');
                setTemplateSelecionado(null);
              }}
              className="w-full sm:w-auto text-xs sm:text-sm"
            >
              Voltar
            </Button>
            <Button
              onClick={handleCopiarEAbrir}
              disabled={!mensagemPersonalizada}
              className="w-full sm:w-auto bg-green-600 hover:bg-green-700 text-xs sm:text-sm"
            >
              {copiado ? (
                <>
                  <Check className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                  Copiado!
                </>
              ) : (
                <>
                  <Send className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                  Enviar
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MessageSquare className="w-5 h-5" />
            Enviar Mensagem
          </DialogTitle>
          <DialogDescription>
            Selecione um template para enviar ao cliente {clienteNome}
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {TEMPLATES_DEFAULT.map((template) => (
            <Card
              key={template.id}
              className="cursor-pointer hover:border-blue-500 hover:shadow-md transition-all"
              onClick={() => handleSelecionarTemplate(template)}
            >
              <CardHeader>
                <CardTitle className="text-base">{template.titulo}</CardTitle>
                <CardDescription className="text-xs">{template.descricao}</CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-xs text-gray-600 line-clamp-3">{template.mensagem}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
